import React, { useState } from 'react'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Paper,
  Divider,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Link,
} from '@mui/material'
import { Upload as UploadIcon } from '@mui/icons-material'

// Define the questionnaire section type
interface QuestionnaireField {
  id?: string
  label: string
  source: string
  required?: boolean
  value?: string
  fieldType?: 'yesno' | 'dropdown' | 'attachment' | 'text'
  options?: Array<{
    text: string
    score?: number // Score from 1-20 for Risk Assessment options
  }>
  // For yes/no fields, we can have default scores
  yesScore?: number
  noScore?: number
  attachments?: Array<{
    id: string
    filename: string
    url?: string
  }>
  vendorId?: number
  questionnaireId?: number
}

interface QuestionnaireSection {
  id?: string
  title: string
  fields: QuestionnaireField[]
}

// Attachment field renderer component
interface AttachmentFieldRendererProps {
  field: QuestionnaireField
  readOnly: boolean
  vendorId?: number
  questionnaireId?: number
}

const AttachmentFieldRenderer: React.FC<AttachmentFieldRendererProps> = ({
  field,
  readOnly,
  vendorId,
  questionnaireId,
}) => {
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  // Function to handle file upload
  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { files } = event.target
    if (!files || files.length === 0) return

    const file = files[0]
    setUploading(true)
    setUploadError(null)

    try {
      // Create form data for the file upload
      const formData = new FormData()
      formData.append('document', file)
      formData.append('field_id', field.id || field.source)

      // Get the token from the URL if this is a public questionnaire
      const token = window.location.pathname.split('/').pop()

      // Determine the API endpoint based on whether this is a public questionnaire
      const apiEndpoint =
        token && token.length > 10
          ? `/api/v1/questionnaire/${token}/attachments`
          : `/api/v1/questionnaires/vendors/${field.vendorId}/${field.questionnaireId}/attachments`

      // Upload the file
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`)
      }

      const data = await response.json()

      // Update the field with the new attachment
      if (!field.attachments) {
        field.attachments = []
      }

      field.attachments.push({
        id: data.id,
        filename: data.original_filename,
        url: `/api/v1/questionnaires/vendors/${field.vendorId}/${field.questionnaireId}/attachments/${data.id}`,
      })

      // Clear the file input
      event.target.value = ''
    } catch (error) {
      console.error('File upload error:', error)
      setUploadError(error instanceof Error ? error.message : 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  return (
    <Box>
      {!readOnly && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={
              uploading ? <CircularProgress size={20} /> : <UploadIcon />
            }
            disabled={uploading || readOnly}
          >
            <Typography>
              {uploading ? 'Uploading...' : 'Add Attachment'}
            </Typography>
            <input
              type="file"
              hidden
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={handleFileUpload}
              disabled={uploading || readOnly}
            />
          </Button>
          {uploadError && (
            <Typography
              color="error"
              variant="caption"
              sx={{ display: 'block', mt: 1 }}
            >
              {uploadError}
            </Typography>
          )}
        </Box>
      )}

      {field.attachments && field.attachments.length > 0 ? (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Uploaded Files:
          </Typography>
          {field.attachments.map((attachment, index) => (
            <Box key={attachment.id || index} sx={{ mb: 1 }}>
              <Link
                href={attachment.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {attachment.filename}
              </Link>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography variant="body2" color="textSecondary">
          {readOnly ? 'No files uploaded' : 'No files uploaded yet'}
        </Typography>
      )}
    </Box>
  )
}

interface QuestionnaireFormLayoutProps {
  questionnaireSections: QuestionnaireSection[]
  questionnaireName?: string
  questionnaireDescription?: string
  readOnly?: boolean
  renderField?: (field: QuestionnaireField) => React.ReactNode
  vendorId?: number
  questionnaireId?: number
  allowAdditionalDocumentUploads?: boolean
}

/**
 * A reusable component for displaying questionnaire layouts
 * Used in OrganisationRiskQuestionnaire preview
 */
const QuestionnaireFormLayout: React.FC<QuestionnaireFormLayoutProps> = ({
  questionnaireSections,
  questionnaireName,
  questionnaireDescription,
  readOnly = false,
  renderField,
  vendorId,
  questionnaireId,
  allowAdditionalDocumentUploads = false,
}) => {
  // Default field renderer for read-only mode
  const defaultReadOnlyFieldRenderer = (field: QuestionnaireField) => {
    return field.value ?? 'N/A'
  }

  // Default field renderer for editable mode
  const defaultEditableFieldRenderer = (field: QuestionnaireField) => {
    // For attachment fields
    if (field.fieldType === 'attachment') {
      return (
        <AttachmentFieldRenderer
          field={field}
          readOnly={readOnly}
          vendorId={vendorId}
          questionnaireId={questionnaireId}
        />
      )
    }

    // For yes/no fields (used in prequalification questionnaires)
    if (field.fieldType === 'yesno') {
      return (
        <TextField
          select
          label={field.label}
          name={field.source}
          fullWidth
          variant="outlined"
          required={field.required}
          disabled={readOnly}
          InputProps={{
            readOnly,
          }}
          helperText={field.required ? 'Required' : 'Optional'}
          SelectProps={{
            native: true,
          }}
        >
          <option value="">Select an option</option>
          <option value="yes">
            Yes
            {field.yesScore !== undefined
              ? ` (Score: ${
                  typeof field.yesScore === 'number'
                    ? field.yesScore.toFixed(1)
                    : field.yesScore
                })`
              : ''}
          </option>
          <option value="no">
            No
            {field.noScore !== undefined
              ? ` (Score: ${
                  typeof field.noScore === 'number'
                    ? field.noScore.toFixed(1)
                    : field.noScore
                })`
              : ''}
          </option>
        </TextField>
      )
    }

    // For dropdown fields (used in risk assessments)
    if (
      field.fieldType === 'dropdown' &&
      field.options &&
      field.options.length > 0
    ) {
      return (
        <TextField
          select
          label={field.label}
          name={field.source}
          fullWidth
          variant="outlined"
          required={field.required}
          disabled={readOnly}
          InputProps={{
            readOnly,
          }}
          helperText={field.required ? 'Required' : 'Optional'}
          SelectProps={{
            native: true,
          }}
        >
          <option value="">Select an option</option>
          {field.options.map((option, index) => (
            <option key={index} value={option.text}>
              {option.text}
              {option.score !== undefined
                ? ` (Score: ${
                    typeof option.score === 'number'
                      ? option.score.toFixed(1)
                      : option.score
                  })`
                : ''}
            </option>
          ))}
        </TextField>
      )
    }

    // For text fields
    if (field.fieldType === 'text') {
      return (
        <TextField
          label={field.label}
          name={field.source}
          fullWidth
          variant="outlined"
          required={field.required}
          disabled={readOnly}
          InputProps={{
            readOnly,
          }}
          multiline
          rows={3}
          helperText={field.required ? 'Required' : 'Optional'}
        />
      )
    }

    // Default text field for other fields
    return (
      <TextField
        label={field.label}
        name={field.source}
        fullWidth
        variant="outlined"
        required={field.required}
        disabled={readOnly}
        InputProps={{
          readOnly,
        }}
        helperText={field.required ? 'Required' : 'Optional'}
      />
    )
  }

  // Use the provided renderField function or default to read-only or editable based on readOnly prop
  const fieldRenderer =
    renderField ||
    (readOnly ? defaultReadOnlyFieldRenderer : defaultEditableFieldRenderer)

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'visible' }}>
      {questionnaireName && (
        <Typography variant="h5" gutterBottom>
          {questionnaireName}
        </Typography>
      )}

      {questionnaireDescription && (
        <Typography variant="body1" color="textSecondary" paragraph>
          {questionnaireDescription}
        </Typography>
      )}

      {questionnaireName && <Divider sx={{ my: 2 }} />}

      {questionnaireSections.map((section, sectionIndex) => (
        <Box key={section.id || sectionIndex} sx={{ mb: 4 }}>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell
                    colSpan={2}
                    sx={{
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      fontWeight: 'bold',
                    }}
                  >
                    {section.title}
                  </TableCell>
                </TableRow>
                {section.fields.map((field, fieldIndex) => (
                  <TableRow key={field.id || fieldIndex}>
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{ width: '30%', verticalAlign: 'top' }}
                    >
                      <Typography variant="body2">
                        {field.label}
                        {field.required && (
                          <Typography
                            component="span"
                            color="error"
                            variant="body2"
                          >
                            *
                          </Typography>
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ width: '70%' }}>
                      {fieldRenderer({
                        ...field,
                        vendorId: vendorId || field.vendorId,
                        questionnaireId:
                          questionnaireId || field.questionnaireId,
                      })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}

      {readOnly && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body1">
            Allow Additional Document Uploads:{' '}
            {allowAdditionalDocumentUploads ? 'Yes' : 'No'}
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default QuestionnaireFormLayout
